import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Train, Car, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Votre message a été envoyé avec succès !");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      toast.error("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: t("contact.address_title"),
      content: (
        <>
          Bâtiment ARCOOP<br />
          Rue des Noirettes 32<br />
          1227 Carouge, Genève
        </>
      ),
    },
    {
      icon: Phone,
      title: t("contact.phone_title"),
      content: (
        <a href="tel:+41223424243" className="text-gold hover:underline text-lg font-heading">
          {t("common.phone_display")}
        </a>
      ),
    },
    {
      icon: Mail,
      title: t("contact.email_title"),
      content: (
        <a
          href="mailto:reservation@brasseriecarougeoise.ch"
          className="block w-full text-center text-gold hover:underline text-base font-body leading-relaxed break-words"
        >
          reservation@brasseriecarougeoise.ch
        </a>
      ),
    },
    {
      icon: Clock,
      title: t("contact.hours_title"),
      content: (
        <>
          Lun - Ven: 6h00 - 23h00<br />
          Sam - Dim: Fermé
        </>
      ),
    },
  ];

  return (
    <div className="pt-20" data-testid="contact-page">
      {/* Hero */}
      <section className="py-20 md:py-28 bg-primary text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src="/images/ambiance/hero3.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-gold font-accent text-lg tracking-widest">
              {t("contact.subtitle")}
            </span>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl mt-2 mb-6">
              {t("contact.title")}
            </h1>
            <div className="section-divider mx-auto bg-gradient-to-r from-transparent via-gold to-transparent" />
          </motion.div>
        </div>
      </section>

      {/* Contact Info Grid */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {contactInfo.map((info, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="h-full border-border/40 card-elegant">
                  <CardContent className="p-6 text-center">
                    <div className="w-14 h-14 mx-auto mb-4 bg-gold/10 rounded-full flex items-center justify-center">
                      <info.icon className="h-7 w-7 text-gold" />
                    </div>
                    <h3 className="font-heading text-lg text-primary mb-3">{info.title}</h3>
                    <div className="text-muted-foreground text-sm font-body min-h-[56px] flex items-center justify-center">
                      {info.content}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Map and Form Grid */}
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Map */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl text-primary mb-6">Plan d'accès</h2>

              <div className="aspect-[4/3] overflow-hidden rounded shadow-lg mb-6">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2762.8654!2d6.1390!3d46.1831!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x478c7ad45a26e41f%3A0x8b2e7b4c5c5e5e5e!2sRue%20des%20Noirettes%2032%2C%201227%20Carouge%2C%20Switzerland!5e0!3m2!1sfr!2sch!4v1699999999999!5m2!1sfr!2sch"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Brasserie Carougeoise - Google Maps"
                  data-testid="contact-google-maps"
                />
              </div>

              {/* Transport Info */}
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 bg-secondary rounded">
                  <Train className="h-5 w-5 text-gold mt-0.5" />
                  <div>
                    <h4 className="font-heading text-primary mb-1">{t("contact.transport_title")}</h4>
                    <p className="text-muted-foreground text-sm font-body">{t("contact.transport")}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-secondary rounded">
                  <Car className="h-5 w-5 text-gold mt-0.5" />
                  <div>
                    <h4 className="font-heading text-primary mb-1">Parking</h4>
                    <p className="text-muted-foreground text-sm font-body">{t("contact.parking")}</p>
                  </div>
                </div>
              </div>

              <a
                href="https://www.google.com/maps/dir//Rue+des+Noirettes+32,+1227+Carouge,+Switzerland"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-6"
              >
                <Button
                  variant="outline"
                  className="border-primary text-primary hover:bg-primary hover:text-white rounded-none px-6 py-5 gap-2"
                  data-testid="contact-directions-btn"
                >
                  <MapPin className="h-4 w-4" />
                  {t("contact.directions")}
                </Button>
              </a>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-2xl text-primary mb-6">{t("contact.form_title")}</h2>

              <form onSubmit={handleSubmit} className="bg-card p-8 shadow-lg border border-border/40">
                <div className="space-y-6">
                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name" className="font-body">{t("contact.form_name")}</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      required
                      className="rounded-none border-border"
                      data-testid="contact-name-input"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="font-body">{t("contact.form_email")}</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      required
                      className="rounded-none border-border"
                      data-testid="contact-email-input"
                    />
                  </div>

                  {/* Subject */}
                  <div className="space-y-2">
                    <Label htmlFor="subject" className="font-body">{t("contact.form_subject")}</Label>
                    <Input
                      id="subject"
                      value={formData.subject}
                      onChange={(e) => handleChange("subject", e.target.value)}
                      className="rounded-none border-border"
                      data-testid="contact-subject-input"
                    />
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <Label htmlFor="message" className="font-body">{t("contact.form_message")}</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleChange("message", e.target.value)}
                      required
                      rows={5}
                      className="rounded-none border-border resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gold hover:bg-gold/90 text-white rounded-none py-6 text-sm tracking-wider uppercase btn-hover-expand"
                    data-testid="contact-submit-btn"
                  >
                    {loading ? (
                      "Envoi en cours..."
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        {t("contact.form_submit")}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
